import { useRef, useState } from "react";
import { Upload, X, FileImage, FileVideo, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/lib/siteConfig";
import { toast } from "sonner";

/**
 * Single-asset uploader for site media (logo / hero image / hero video).
 * Calls /api/admin/upload (admin only).
 */
export default function MediaUploader({
  fileId,
  onChange,
  accept = "image/*,video/*",
  label = "Upload media",
  testId = "media-uploader",
  hint = "JPG, PNG, GIF, WebP, SVG, MP4, WebM (max 50MB for video, 15MB for images)",
  showPreview = true,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data);
      toast.success(`Uploaded ${data.original_filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = () => onChange?.(null);
  const url = fileId ? fileUrl(fileId) : null;
  const isVideo = url && /\.(mp4|webm|mov)$/i.test(url);

  return (
    <div data-testid={testId}>
      {showPreview && url && (
        <div className="mb-3 relative inline-block">
          {isVideo ? (
            <video src={url} className="max-h-40 rounded-sm border border-line bg-cream" controls />
          ) : (
            <img src={url} alt="preview" className="max-h-40 rounded-sm border border-line bg-cream object-contain" />
          )}
          <button
            type="button"
            onClick={remove}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-danger text-bone flex items-center justify-center hover:bg-danger/90"
            data-testid={`${testId}-remove`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        className="border border-dashed border-line bg-cream/40 hover:bg-cream rounded-sm p-5 text-center cursor-pointer transition-colors duration-200"
        data-testid={`${testId}-dropzone`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-sage">
            <Loader2 className="w-5 h-5 animate-spin text-clay" />
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-sage">
            <Upload className="w-5 h-5 text-clay" strokeWidth={1.5} />
            <span className="text-sm font-medium text-forest">{url ? "Replace" : label}</span>
            <span className="text-xs">{hint}</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}
